import { handleActions } from 'redux-actions';
import {
  checkUserFailure, identifyUserStart, identifyUserSuccess, identifyUserFailure,
  identifyUserFailureAccessNotAllowedByEmployer, identifyUserFailureAccessNotAllowedByEmployerUserNoted,
  selectEmployerDepartment, confirmContinue, clearSessionSuccess, restoreInitialState, sessionTimeOut,
  setFlagCreateUserIfNotExists, setFlagRequiresEmployer, setUserPreRegistrationData, registrationUserStart,
  registrationUserSuccess, registrationUserFailure, identityUserFailureDueToMultipleEmployers,
  selectMainEmployer, selectEmployer, bankAuthenticationStarted, bankAuthenticationLinkReceived
} from '../actions/session';
import { changeLanguage } from '../actions/app';

const initialState = {
  userIdentified: false,
  currentlyIdentifying: false,
  doneIdentifying: false,
  loginFailed: false,
  loginFailedMessage: null,
  requireContinueConfirmation: false,
  userConfirmedContinue: false,
  hasEmployer: false,
  hasDepartment: false,
  selectedEmployer: -1,
  selectedDepartment: -1,
  sessionCleared: false,
  sessionTimeout: false,
  user: {
    employers: []
  },
  createUserIfNotExists: false,
  requiresEmployer: false,
  prepareUserRegistration: false,
  userRegistrationData: {
    firstName: '',
    lastName: '',
    personId: ''
  },
  userRegistered: false,
  currentlyRegistering: false,
  registrationFailed: false,
  registrationFailedMessage: null,
  loginFailedNotAllowedByEmployer: false,
  loginFailedNotDueToMultipleEmployers: false,
  messageLocalizationKey: '',
  viaSso: false,
  language: '',
  triggerInitialScroll: false,
  bankRedirectingInProcess: false,
  creationIncomplete: false
};

const getAgreementLevelActionFromMainEmployer = (user) => {
  if (user.employers.length > 0) {
    const mainEmployer = user.employers.filter(e => e.mainEmployer);
    if (mainEmployer.length > 0) {
      const employer = mainEmployer[0];
      const { organizationAgreementLevel } = employer;
      if (organizationAgreementLevel) {
        return organizationAgreementLevel.action === 'SHOW_NOTIFICATION';
      }
    }
  }
  return false;
};

// Set first main employer as a user's selected employer.
// When/if we later have a feature for user to select employer, we have to change this logic
const getFirstMainEmployerAsSelectedEmployer = (user) => {
  let employerIndex = initialState.selectedEmployer;
  if (user.employers.length > 0) {
    // take the first one and then try to find the main employer
    employerIndex = 0;
    for (let i = 0; i < user.employers.length; i += 1) {
      if (user.employers[i].mainEmployer) {
        employerIndex = i;
        break;
      }
    }
  }
  return employerIndex;
};

const sessionReducer = handleActions({
  [identifyUserStart]: state => ({
    ...state,
    currentlyIdentifying: true,
    doneIdentifying: false,
    loginFailed: false,
    loginFailedMessage: initialState.loginFailedMessage
  }),
  [identifyUserSuccess]: (state, action) => {
    if (action.payload.viaSso && action.payload.language) {
      changeLanguage(action.payload.language);
    }
    return {
      ...state,
      currentlyIdentifying: false,
      doneIdentifying: true,
      userIdentified: true,
      triggerInitialScroll: true,
      user: Object.assign(initialState.user, action.payload),
      hasEmployer: action.payload.employers && action.payload.employers.length > 0,
      viaSso: action.payload.viaSso,
      language: action.payload.language,
      requireContinueConfirmation: getAgreementLevelActionFromMainEmployer(action.payload),
      creationIncomplete: action.payload.creationIncomplete
    };
  },
  [selectMainEmployer]: (state, action) => ({
    ...state,
    selectedEmployer: getFirstMainEmployerAsSelectedEmployer(action.payload)
  }),
  [selectEmployer]: (state, action) => ({
    ...state,
    selectedEmployer: action.payload // employerIndex

  }),
  [checkUserFailure]: state => ({
    ...state,
    currentlyIdentifying: false,
    doneIdentifying: true
  }),
  [identifyUserFailure]: (state, action) => ({
    ...state,
    currentlyIdentifying: false,
    doneIdentifying: true,
    loginFailed: true,
    loginFailedMessage: action.payload
  }),
  [identifyUserFailureAccessNotAllowedByEmployer]: (state, action) => ({
    ...state,
    currentlyIdentifying: false,
    doneIdentifying: true,
    loginFailed: false,
    loginFailedMessage: '',
    loginFailedNotAllowedByEmployer: true,
    messageLocalizationKey: action.payload
  }),
  [identifyUserFailureAccessNotAllowedByEmployerUserNoted]: state => ({
    ...state,
    loginFailedNotAllowedByEmployer: false
  }),
  [selectEmployerDepartment]: (state, action) => ({
    ...state,
    hasEmployer: action.payload.hasEmployer,
    hasDepartment: action.payload.hasDepartment,
    // selectedEmployer: action.payload.employer,
    selectedDepartment: action.payload.department
  }),
  [confirmContinue]: state => ({
    ...state,
    userConfirmedContinue: true
  }),
  [clearSessionSuccess]: state => ({
    ...initialState,
    // requiresEmployer: state.requiresEmployer,
    // createUserIfNotExists: state.createUserIfNotExists,
    sessionCleared: true,
    user: {
      employers: []
    }
  }),
  [restoreInitialState]: state => ({
    ...initialState, // restore the original state
    // requiresEmployer: state.requiresEmployer,
    // createUserIfNotExists: state.createUserIfNotExists
    user: {
      employers: []
    }
  }),
  [sessionTimeOut]: state => ({
    ...initialState,
    // requiresEmployer: state.requiresEmployer,
    // createUserIfNotExists: state.createUserIfNotExists,
    sessionTimeout: true,
    user: {
      employers: []
    }
  }),
  [setFlagCreateUserIfNotExists]: (state, action) => ({
    ...state,
    createUserIfNotExists: action.payload
  }),
  [setFlagRequiresEmployer]: (state, action) => ({
    ...state,
    requiresEmployer: action.payload
  }),
  [setUserPreRegistrationData]: (state, action) => ({
    ...state,
    currentlyIdentifying: false,
    currentlyRegistering: false,
    loginFailed: false,
    prepareUserRegistration: true,
    userRegistrationData: action.payload
  }),
  [registrationUserStart]: state => ({
    ...state,
    userRegistered: false,
    currentlyRegistering: true,
    registrationFailed: false,
    registrationFailedMessage: null
  }),
  [registrationUserSuccess]: state => ({
    ...state,
    userRegistered: true,
    currentlyRegistering: false,
    registrationFailed: false,
    registrationFailedMessage: null
  }),
  [registrationUserFailure]: state => ({
    ...state,
    userRegistered: false,
    currentlyRegistering: false,
    registrationFailed: true,
    registrationFailedMessage: 'registrationError'
  }),
  [identityUserFailureDueToMultipleEmployers]: state => ({
    ...state,
    loginFailedNotDueToMultipleEmployers: true,
    messageLocalizationKey: 'loginFailedDueToMultipleEmployers'
  }),
  [bankAuthenticationStarted]: state => ({
    ...state,
    bankRedirectingInProcess: true
  }),
  [bankAuthenticationLinkReceived]: state => ({
    ...state,
    bankRedirectingInProcess: false
  })
}, initialState);

export default sessionReducer;



// WEBPACK FOOTER //
// ./src/reducers/session.js